/**
 * Offerings entity hooks — uses Supabase REST API.
 * Works without GraphQL codegen (offerings table must exist in DB).
 *
 * This module is the public entry point for offerings hooks. Implementation
 * is split across:
 *   - offerings-query-keys.ts  — shared TanStack Query keys
 *   - offerings-fetchers.ts    — raw Supabase fetchers/mutators
 *   - use-offering-queries.ts  — read hooks (paginated, infinite, single)
 *   - use-offering-mutations.ts — write hooks (create, update, delete)
 *   - offerings-config.ts      — static field/table config
 *
 * Keep this file thin: it MUST preserve the same `useOfferingHooks` API
 * surface and the same TanStack Query cache keys callers depend on.
 */

import type { Offering } from "@/lib/types/database/offerings";
import type { EntityHooks } from "@/components/shared/UniversalCRMTable/types/shared";

import {
  usePaginatedOfferings,
  useInfiniteOfferings,
  useSingleOffering,
} from "./use-offering-queries";
import {
  useCreateOffering,
  useUpdateOffering,
  useDeleteOffering,
} from "./use-offering-mutations";
import { getOfferingFieldConfig, getOfferingTableConfig } from "./offerings-config";

export const useOfferingHooks = (): EntityHooks<Offering> => ({
  usePaginated: usePaginatedOfferings,
  useInfinite: useInfiniteOfferings,
  useSingle: useSingleOffering,
  useCreate: useCreateOffering,
  useUpdate: useUpdateOffering,
  useDelete: useDeleteOffering,
  getFieldConfig: getOfferingFieldConfig,
  getTableConfig: getOfferingTableConfig,
});
